import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Newspaper, Send, Trash2, Linkedin, CheckCircle2, ImagePlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { API, authHeaders } from "@/lib/api";

export const InsightsPanel = ({ onAuthError }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [form, setForm] = useState({ title: "", tag: "Update", body: "" });
  const [linkedin, setLinkedin] = useState({ configured: false, connected: false });
  const [postingId, setPostingId] = useState(null);

  const loadLinkedIn = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/linkedin/status`, { headers: authHeaders() });
      setLinkedin(res.data);
    } catch {
      /* not configured */
    }
  }, []);

  const connectLinkedIn = async () => {
    try {
      const res = await axios.get(`${API}/linkedin/auth-url`, { headers: authHeaders() });
      window.open(res.data.url, "_blank", "noopener");
    } catch (err) {
      toast.error(err.response?.data?.detail || "LinkedIn is not configured yet");
    }
  };

  const postToLinkedIn = async (id) => {
    setPostingId(id);
    try {
      const res = await axios.post(`${API}/posts/${id}/linkedin`, {}, { headers: authHeaders() });
      setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, linkedin_post_id: res.data.linkedin_post_id } : p)));
      toast.success(res.data.status === "already_posted" ? "Already posted to LinkedIn" : "Posted to LinkedIn");
    } catch (err) {
      if (err.response?.status === 401) return onAuthError();
      toast.error(err.response?.data?.detail || "LinkedIn post failed");
    } finally {
      setPostingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/posts`);
      setPosts(res.data.posts);
    } catch {
      toast.error("Could not load insights");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadLinkedIn(); }, [load, loadLinkedIn]);

  const publish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/posts`, form, { headers: authHeaders() });
      setPosts((ps) => [res.data, ...ps]);
      setForm({ title: "", tag: "Update", body: "" });
      toast.success("Insight published");
    } catch (err) {
      if (err.response?.status === 401) return onAuthError();
      toast.error("Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const generateImage = async (id) => {
    setPostingId(id);
    try {
      const res = await axios.post(`${API}/posts/${id}/image`, {}, { headers: authHeaders() });
      setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, image_url: res.data.image_url } : p)));
      toast.success("Cover image generated");
    } catch (err) {
      if (err.response?.status === 401) return onAuthError();
      toast.error(err.response?.data?.detail || "Image generation failed");
    } finally {
      setPostingId(null);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/posts/${id}`, { headers: authHeaders() });
      setPosts((ps) => ps.filter((p) => p.id !== id));
      toast.success("Insight deleted");
    } catch (err) {
      if (err.response?.status === 401) return onAuthError();
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="insights-panel" className="grid lg:grid-cols-12 gap-8">
      {linkedin.configured && !linkedin.connected && (
        <div data-testid="linkedin-connect-banner" className="lg:col-span-12 bg-slate2 border border-gold/40 p-5 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-[#A0AAB2]">Connect your LinkedIn company page to auto-post every article and daily snapshot.</p>
          <button
            data-testid="linkedin-connect-button"
            onClick={connectLinkedIn}
            className="bg-[#0A66C2] text-white hover:bg-[#0b5cb0] transition-colors rounded-sm px-5 py-2.5 text-sm font-semibold flex items-center gap-2"
          >
            <Linkedin size={15} /> Connect LinkedIn
          </button>
        </div>
      )}
      <form onSubmit={publish} data-testid="insight-form" className="lg:col-span-5 bg-slate2 border border-white/10 p-8 space-y-5 h-max">
        <h2 className="font-serif text-xl">Publish an insight</h2>
        <div>
          <Label htmlFor="insight-title" className="text-sm text-[#A0AAB2] mb-2 block">Title</Label>
          <Input
            data-testid="insight-title-input"
            id="insight-title"
            required
            minLength={4}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="RBI holds repo rate — what it means for borrowers"
            className="bg-slate3 border-white/10 rounded-sm"
          />
        </div>
        <div>
          <Label htmlFor="insight-tag" className="text-sm text-[#A0AAB2] mb-2 block">Tag</Label>
          <Input
            data-testid="insight-tag-input"
            id="insight-tag"
            value={form.tag}
            onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
            placeholder="Rate Update"
            className="bg-slate3 border-white/10 rounded-sm"
          />
        </div>
        <div>
          <Label htmlFor="insight-body" className="text-sm text-[#A0AAB2] mb-2 block">Body</Label>
          <Textarea
            data-testid="insight-body-input"
            id="insight-body"
            required
            minLength={10}
            rows={6}
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            placeholder="Write your note…"
            className="bg-slate3 border-white/10 rounded-sm"
          />
        </div>
        <button
          data-testid="insight-publish-button"
          type="submit"
          disabled={publishing}
          className="bg-gold text-night hover:bg-goldhover transition-colors rounded-sm px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60"
        >
          {publishing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {publishing ? "Publishing…" : "Publish"}
        </button>
      </form>

      <div className="lg:col-span-7">
        {loading ? (
          <div className="flex justify-center py-16" data-testid="insights-panel-loading"><Loader2 className="animate-spin text-gold" size={26} /></div>
        ) : posts.length === 0 ? (
          <div className="bg-slate2 border border-white/10 py-20 text-center" data-testid="insights-panel-empty">
            <Newspaper size={26} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-sm">No insights published yet.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="insights-panel-list">
            {posts.map((post) => (
              <div key={post.id} data-testid={`insight-row-${post.id}`} className="bg-slate2 border border-white/10 p-5 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-gold uppercase tracking-[0.2em] mb-1">{post.tag}</p>
                  <p className="font-medium text-foreground">{post.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    data-testid={`insight-image-${post.id}`}
                    onClick={() => generateImage(post.id)}
                    disabled={postingId === post.id}
                    title={post.image_url ? "Regenerate cover image" : "Generate cover image"}
                    className={`text-xs border rounded-sm px-3 py-2 flex items-center gap-1.5 transition-colors disabled:opacity-60 ${
                      post.image_url
                        ? "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10"
                        : "border-white/15 text-muted-foreground hover:text-gold hover:border-gold/40"
                    }`}
                  >
                    {postingId === post.id ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                    {post.image_url ? "Image ✓" : "Image"}
                  </button>
                  {post.linkedin_post_id ? (
                    <span data-testid={`linkedin-posted-${post.id}`} className="text-xs text-emerald-300 flex items-center gap-1.5">
                      <CheckCircle2 size={14} /> On LinkedIn
                    </span>
                  ) : linkedin.connected ? (
                    <button
                      data-testid={`linkedin-post-${post.id}`}
                      onClick={() => postToLinkedIn(post.id)}
                      disabled={postingId === post.id}
                      className="text-xs border border-[#0A66C2] text-[#6fb2f0] hover:bg-[#0A66C2]/15 transition-colors rounded-sm px-3 py-2 flex items-center gap-1.5 disabled:opacity-60"
                    >
                      {postingId === post.id ? <Loader2 size={13} className="animate-spin" /> : <Linkedin size={13} />}
                      Post to LinkedIn
                    </button>
                  ) : null}
                  <button
                    data-testid={`insight-delete-${post.id}`}
                    onClick={() => remove(post.id)}
                    className="text-muted-foreground hover:text-red-400 transition-colors"
                    aria-label="Delete insight"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
